import {ReactNode} from "react";
import {DateRange} from "react-day-picker";
import {BoxProps, TextareaProps} from "@chakra-ui/react";
import CompanyInfo from "./Company";
import {Request, RequestAPIResponse, Verification} from "./Request";
import SuggestionData from "./Suggestion";
import {formatDate} from "../helper/utils";
import {Chat, GroupResult, Message, MessageItem, MessageGroup, Participant} from "./Chat";


type Action = {label: string, link: string};
export type PopupInfo = {
    action?: Action
    desc: string
    node?: ReactNode
    title: string
    icon?: string
    iconFill?: string
};
export type TagUser = {email: string, type: "verified"|"unverified"|"non-existent"};
export type RequestState= "not-sent"|"pending"|"verified"|"failed"
type Nullable<T> = T | null | undefined;
export type PickerProps<T> = {onSubmit: (v: T) => void, picked?: T};
const statuses: Record<Request["status"], string> = {
    approved: "proceeding",
    declined: "not proceeding",
    cancelled: "cancelled",
    open: "awaiting resolution"
}

export type RequestVerification =
| {type: "identity-verified"}
| {type: "biometric-setup"};
export type RequestAction =
| {type: "close"}
| {type: "view_infos"}
| {type: "cancel"}
| {type: "read-messages"}
| {data: {note?: string, proceed: boolean}, type: "resolve"}
| {data: {email: string}, type: "reassign"}
| {data: {users: TagUser[]}, type: "add_participants"};
type Structure =
| {data: {onAction: (v: RequestAction) => void}, type: "reassign"}
| {data: {onAction: (v: RequestAction) => void}, type: "resolve"}
| {type: "request", data: {
    canCancel: boolean
    chat: Chat
    checkStatus: (v: RequestVerification) => boolean
    currentEmail: string
    isLoading?: boolean
    onAction: (v: RequestAction) => void
    request: Request
}};

type User = {
    CompanyId: string;
    role: string;
    userID: string;
    createdAt: string;
    email: string;
    name: string;
    profileImage?: string;
	verificationStatus?:
		| "verified"
		| "started"
		| "failed_name_mismatch"
		| "failed_country_mismatch"
		| "failed_scan_error"
		| "pending"
		| "unknown";
    hasAccess?: boolean
};

type DateRangeError = {start?: string, end?: string};
export type Option = {name: string; id: string};
export type DefaultProps<T> = {
    name?: string
    label?: string
    error?: string
    placeholder?: string
    defaultValue?: T
    disabled?: boolean
    currentValue?: T
    description?: string
    mh?: number
    onUpdate?: (val: T) => void
    onSizeExceed?: (v: boolean) => void
}
type InputProps<T> = DefaultProps<T> & BoxProps;
export type UserTagProps = {
    onAddition: (v: TagUser) => void
    onRemoval: (v: string) => void
}

type InputStyle =
| {type: "datetime", data: InputProps<Date> & {whenReset: () => void}}
| {type: "users", data: InputProps<TagUser[]> & UserTagProps}
| {type: "date_range", data: InputProps<DateRange> & {errors?: DateRangeError}}
| {type: "select", data: InputProps<string> & {options: Option[]}}
| {type: "text+box", data: InputProps<string> & {rightLabel: string}}
| {type: "toggle", data: InputProps<boolean>}
| {type: "textarea", data: DefaultProps<string> & TextareaProps}
| {type: "text" | "email", data: InputProps<string>};
export type ItemProps = {
    s?: string
    icon?: string
    link?: string
    value?: string
    node?: ReactNode
};
type TableColumn<T> = {
    header: string
    sticky?: boolean
    accessor: keyof T | ((row: T) => ReactNode)
};
export type InfoBoxItem = {
    items: ItemProps[],
    noCase?: boolean,
    title: string
};
type InfoBoxProps = {
    icon: string;
    items: InfoBoxItem[]
    node?: ReactNode,
    noCase?: boolean
    title: string
} & BoxProps;

export function requestInfos(r: Request): InfoBoxProps {
    return {
        icon: "mail",
        items: [
            {title: "title", items:[{value: r.title}]},
            {title: "description", items:[{value: r.description}]},
            {title: "reference", items:[{value: r.loadID ?? "--"}]},
            {title: "creation Date", items:[{value: formatDate(r.createTime)}]},
            {title: "approver email", items:[{value: r.approver}]},
            {title: "status", items:[{value: statuses[r.status]}]}
        ],
        title: "Request"
    };
}

export type {
    CompanyInfo,
    InputProps,
    InputStyle,
    InfoBoxProps,
    GroupResult,
    MessageGroup,
    Message,
    MessageItem,
    Nullable,
    Request,
    Structure,
    SuggestionData,
    TableColumn,
    User,
    Verification
};
export {
    statuses,
    Chat,
    Participant,
    RequestAPIResponse
}
